import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export const useContributorAdmin = () => {
  const [contributors, setContributors] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [monthlyRevenue, setMonthlyRevenue] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [settings, setSettings] = useState({ eligibility_threshold: 100, revenue_share_percentage: 11 });
  const [savingSettings, setSavingSettings] = useState(false);
  
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  
  const fetchContributors = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/contributors/list`, { headers });
      setContributors(response.data);
    } catch (error) {
      toast.error('Failed to fetch contributors');
    } finally {
      setLoading(false);
    }
  }, []);
  
  const fetchAllUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/users?limit=1000`, { headers });
      const data = response.data;
      setAllUsers(Array.isArray(data) ? data : data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, []);
  
  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/contributors/settings`, { headers });
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  }, []);
  
  const fetchMonthlyRevenue = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/contributors/revenue/${selectedMonth}`, { headers });
      setMonthlyRevenue(response.data);
    } catch (error) {
      console.error('Failed to fetch revenue:', error);
    }
  }, [selectedMonth]);
  
  const fetchPayouts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/contributors/payouts/${selectedMonth}`, { headers });
      setPayouts(response.data);
    } catch (error) {
      console.error('Failed to fetch payouts:', error);
    }
  }, [selectedMonth]);
  
  useEffect(() => {
    fetchContributors();
    fetchAllUsers();
    fetchSettings();
  }, [fetchContributors, fetchAllUsers, fetchSettings]);
  
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/contributors/settings`, {
        eligibility_threshold: settings.eligibility_threshold,
        revenue_share_percentage: settings.revenue_share_percentage
      }, { headers });
      toast.success('Settings saved successfully');
      fetchContributors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };
  
  const handleAssignContributor = async (userId) => {
    try {
      await axios.post(`${API}/contributors/assign`, { user_id: userId }, { headers });
      toast.success('Contributor role assigned successfully');
      fetchContributors();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign contributor role');
      return false;
    }
  };
  
  const handleRemoveContributor = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this contributor?')) return;
    
    try {
      await axios.post(`${API}/contributors/remove/${userId}`, {}, { headers });
      toast.success('Contributor role removed');
      fetchContributors();
    } catch (error) {
      toast.error('Failed to remove contributor role');
    }
  };
  
  const handleAdjustPoints = async (userId, points, reason) => {
    if (!reason) {
      toast.error('Please provide a reason for the adjustment');
      return false;
    }
    
    try {
      await axios.post(`${API}/contributors/points/adjust`, {
        user_id: userId,
        points,
        reason
      }, { headers });
      
      toast.success(`Points ${points >= 0 ? 'added' : 'deducted'} successfully`);
      fetchContributors();
      return true;
    } catch (error) {
      toast.error('Failed to adjust points');
      return false;
    }
  };
  
  const handleFinalizeRevenue = async () => {
    if (!window.confirm('Are you sure you want to finalize revenue for this month? This cannot be undone.')) return;
    
    try {
      await axios.post(`${API}/contributors/revenue/${selectedMonth}/finalize`, {}, { headers });
      toast.success('Revenue finalized');
      fetchMonthlyRevenue();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to finalize revenue');
    }
  };
  
  const handleManualRevenue = async (amount, fees, notes) => {
    const parsedAmount = parseFloat(amount);
    const parsedFees = parseFloat(fees) || 0;
    
    if (isNaN(parsedAmount) || parsedAmount < 0) {
      toast.error('Please enter a valid revenue amount');
      return false;
    }
    
    try {
      await axios.post(`${API}/contributors/revenue/${selectedMonth}/manual`, {
        total_membership_revenue: parsedAmount,
        processing_fees: parsedFees,
        notes: notes || null
      }, { headers });
      toast.success('Manual revenue entry saved');
      fetchMonthlyRevenue();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save manual revenue');
      return false;
    }
  };
  
  const handleGeneratePayouts = async () => {
    if (!window.confirm('Generate payouts for this month? Make sure revenue is finalized.')) return;
    
    try {
      const response = await axios.post(`${API}/contributors/payouts/generate/${selectedMonth}`, {}, { headers });
      toast.success(`Generated ${response.data.payouts_generated} payout records`);
      fetchPayouts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate payouts');
    }
  };
  
  const handleUpdatePayout = async (payoutId, status, checkNumber = null) => {
    try {
      await axios.put(`${API}/contributors/payouts/${payoutId}`, {
        status,
        payout_date: status === 'completed' ? new Date().toISOString() : null,
        check_number: checkNumber
      }, { headers });
      
      toast.success('Payout updated');
      fetchPayouts();
    } catch (error) {
      toast.error('Failed to update payout');
    }
  };
  
  return {
    // State
    contributors,
    allUsers,
    loading,
    selectedMonth,
    setSelectedMonth,
    monthlyRevenue,
    payouts,
    settings,
    setSettings,
    savingSettings,
    
    // Actions
    fetchMonthlyRevenue,
    fetchPayouts,
    handleSaveSettings,
    handleAssignContributor,
    handleRemoveContributor,
    handleAdjustPoints,
    handleFinalizeRevenue,
    handleManualRevenue,
    handleGeneratePayouts,
    handleUpdatePayout,
  };
};

// Helper functions
export const getMonthOptions = () => {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return months;
};

export const formatMonth = (monthStr) => {
  const [year, month] = monthStr.split('-');
  const date = new Date(year, parseInt(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};
